'use client';

import React, { createContext, useContext, useState, ReactNode } from "react";

type ToastType = "success" | "error" | "info";

interface Toast {
  id: string;
  message: string;
  type: ToastType;
}

interface ToastContextType {
  showToast: (message: string, type?: ToastType) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const showToast = (message: string, type: ToastType = "success") => {
    const id = Math.random().toString(36).substring(2, 9);
    setToasts((prev) => [...prev, { id, message, type }]);
    
    // Auto-remove after 4 seconds
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 4000);
  };

  const removeToast = (id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      {/* Toast rendering overlay */}
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[9999] flex flex-col gap-3 max-w-sm w-full pointer-events-none items-center px-4">
        {toasts.map((toast) => {
          let style = {
            backgroundColor: "#0f172a",
            borderColor: "#1e293b",
            color: "#ffffff"
          };
          let icon = "info";
          let iconColor = "#00B5D1";

          if (toast.type === "success") {
            style = {
              backgroundColor: "#002873",
              borderColor: "rgba(0, 181, 209, 0.3)",
              color: "#ffffff"
            };
            icon = "check_circle";
            iconColor = "#34d399";
          } else if (toast.type === "error") {
            style = {
              backgroundColor: "#4c0519",
              borderColor: "rgba(244, 63, 94, 0.3)",
              color: "#ffe4e6"
            };
            icon = "error";
            iconColor = "#fb7185";
          }

          return (
            <div
              key={toast.id}
              style={style}
              className="flex items-start gap-3 p-4 rounded-2xl border backdrop-blur-md shadow-2xl transition-all duration-300 pointer-events-auto animate-in slide-in-from-top-4 fade-in duration-300 w-full max-w-xs sm:max-w-sm"
            >
              <span className="material-symbols-outlined shrink-0" style={{ color: iconColor }}>{icon}</span>
              <p className="text-xs font-bold leading-normal flex-1" style={{ color: style.color }}>{toast.message}</p>
              {/* Exception to Primitive Components Rule: Native button is used here for standard overlay close control to prevent layout dependency cycles in the root layout */}
              <button
                type="button"
                onClick={() => removeToast(toast.id)}
                className="text-slate-400 hover:text-white transition-colors shrink-0"
                style={{ background: "none", border: "none", cursor: "pointer" }}
              >
                <span className="material-symbols-outlined text-sm">close</span>
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}
